import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { universityService } from '../services/universityService';
import { editionService } from '../services/editionService';
import { University, SupportUniversityPayload, SupportUniversityResponse } from '../types/university.types';
import { Alert, Loader, Confirm } from '../components/common';
import { useAuth } from '../hooks/useAuth';
import { getMediaUrl } from '../config/api.config';
import { motion } from 'framer-motion';

export const HomePage: React.FC = () => {
  const [universities, setUniversities] = useState<University[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeEditionId, setActiveEditionId] = useState<number | null>(null);
  const [votingInProgress, setVotingInProgress] = useState<number | null>(null);
  const [voteSuccess, setVoteSuccess] = useState<{id: number, message: string} | null>(null);
  const [voteError, setVoteError] = useState<{id: number, message: string} | null>(null);
  const [confirmVote, setConfirmVote] = useState<{isOpen: boolean, universityId: number | null, universityName: string}>(
    {isOpen: false, universityId: null, universityName: ''}
  );
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        // Récupérer l'édition active
        const editionResponse = await editionService.getActiveEditionInfo();
        if (editionResponse.data && editionResponse.data.edition) {
          setActiveEditionId(editionResponse.data.edition.id);
        }
        
        // Récupérer les universités
        const universitiesResponse = await universityService.getActiveVenueUniversities();
        setUniversities(universitiesResponse.data);
        setError(null);
      } catch (error) {
        if (error instanceof Error) {
          setError(error.message);
        } else {
          setError('Une erreur est survenue lors de la récupération des données');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);
  
  // Rafraîchir la liste des universités après un vote réussi
  useEffect(() => {
    if (voteSuccess) {
      const timer = setTimeout(() => {
        setVoteSuccess(null);
        // Rafraîchir la liste des universités
        universityService.getActiveVenueUniversities()
          .then(response => setUniversities(response.data))
          .catch(err => console.error('Erreur lors du rafraîchissement des universités:', err));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [voteSuccess]);
  
  // Effacer le message d'erreur après un délai
  useEffect(() => {
    if (voteError) {
      const timer = setTimeout(() => {
        setVoteError(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [voteError]);

  const navigateToUniversityDetails = (universityId: number, event: React.MouseEvent) => {
    // Permettre la navigation depuis le bouton de détails
    // et empêcher la navigation depuis la carte si le clic vient du bouton de vote
    const target = event.target as HTMLElement;
    const isFromVoteButton = target.closest('button[aria-label="Supporter cette université"]');
    
    if (isFromVoteButton) {
      return;
    }
    
    navigate(`/universites/${universityId}`);
  };

  const handleVoteClick = (universityId: number, event?: React.MouseEvent) => {
    // Si l'événement est fourni, empêcher la propagation pour éviter la navigation
    if (event) {
      event.stopPropagation();
    }
    // Animation de clic pour feedback visuel immédiat
    const targetButton = document.getElementById(`vote-btn-${universityId}`);
    if (targetButton) {
      targetButton.classList.add('scale-95');
      setTimeout(() => targetButton.classList.remove('scale-95'), 200);
    }
    
    if (!isAuthenticated) {
      // Rediriger vers la page d'authentification si l'utilisateur n'est pas connecté
      navigate('/auth', { state: { redirectTo: '/', universityId } });
      return;
    }
    
    if (!activeEditionId) {
      setVoteError({id: universityId, message: 'Impossible de voter: aucune édition active'});
      return;
    }
    
    // Vérifier si l'utilisateur a déjà voté pour cette université
    const university = universities.find(u => u.id === universityId);
    if (!university) {
      setVoteError({id: universityId, message: 'Université non trouvée'});
      return;
    }
    
    if (university.user_has_voted) {
      setVoteError({id: universityId, message: 'Vous avez déjà voté pour cette université'});
      return;
    }
    
    // Ouvrir la boîte de dialogue de confirmation
    setConfirmVote({
      isOpen: true,
      universityId: universityId,
      universityName: university.name
    });
  };
  
  const processVote = async (universityId: number) => {
    if (!universityId || !activeEditionId) return;
    
    try {
      setVotingInProgress(universityId);
      
      const payload: SupportUniversityPayload = {
        university_id: universityId,
        edition_id: activeEditionId
      };
      
      const response = await universityService.supportUniversity(payload);
      
      if (response.status === 'success') {
        setVoteSuccess({id: universityId, message: response.message || 'Vote enregistré avec succès!'});
        
        // Mettre à jour l'université dans la liste locale
        setUniversities(prevUniversities => 
          prevUniversities.map(u => 
            u.id === universityId 
              ? { ...u, user_has_voted: true, supporters_count: u.supporters_count + 1 }
              : u
          )
        );
      } else {
        // Gestion des erreurs avec le format spécifique
        let errorMessage = response.message || 'Erreur lors du vote';
        
        // Vérifier si nous avons une erreur spécifique au vote dans le champ errors.vote
        if (response.errors && response.errors.vote) {
          errorMessage = response.errors.vote;
        }
        
        setVoteError({id: universityId, message: errorMessage});
      }
    } catch (error) {
      let errorMessage = 'Une erreur est survenue lors du vote';
      
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null) {
        // Vérifier si nous avons le format d'erreur spécifique de l'API
        if ('message' in error) {
          errorMessage = (error as {message: string}).message;
        }
        
        // Vérifier si nous avons des erreurs détaillées dans le format spécifique
        if ('errors' in error && typeof (error as any).errors === 'object') {
          const errors = (error as any).errors;
          if (errors && errors.vote) {
            errorMessage = errors.vote;
          }
        }
      }
      
      setVoteError({id: universityId, message: errorMessage});
    } finally {
      setVotingInProgress(null);
      // Fermer la boîte de dialogue de confirmation
      setConfirmVote({isOpen: false, universityId: null, universityName: ''});
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[70vh] py-12 bg-gradient-to-b from-blue-50 to-white">
        <Loader size="large" color="primary" text="Chargement des universités..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-12 px-4 flex justify-center items-center min-h-[70vh] bg-gradient-to-b from-red-50 to-white">
        <Alert type="error" message={error} />
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-blue-50 to-white min-h-screen pb-16">
      <div className="pt-6 pb-8 md:pt-10 md:pb-12 text-center px-4 max-w-6xl mx-auto">
        <motion.h1 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-secondary mb-3 bg-clip-text text-transparent bg-gradient-to-r from-yellow-500 to-yellow-700">
          Fan Club Challenge
        </motion.h1>
        <motion.p 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-lg md:text-xl text-gray-700 max-w-2xl mx-auto font-medium">
          Supportez votre université lors des jeux universitaires et faites la différence !
        </motion.p>
      </div>
      
      <div className="w-full max-w-6xl mx-auto px-4">

      {universities.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-100">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <p className="text-xl text-gray-600 font-medium">Aucune université disponible pour le moment.</p>
          <p className="text-gray-500 mt-2">Revenez bientôt pour découvrir les universités participantes.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {universities.map((university, index) => (
            <motion.div 
              key={university.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -5 }}
              className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col relative"
            >
              <div className="p-5">
                <div className="flex items-start">
                  <div className="mr-4 flex-shrink-0">
                    <img 
                      src={getMediaUrl(university.logo)} 
                      alt={`Logo ${university.name}`} 
                      className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover border-2 border-yellow-400 shadow-md"
                    />
                  </div>
                  <div className="min-w-0 flex-1"> {/* Permet au contenu de se rétrécir si nécessaire */}
                    <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-1 break-words line-clamp-2 hyphens-auto">
                      {university.name}
                    </h2>
                    <div className="flex items-center text-yellow-500 text-sm font-medium">
                      <svg className="w-5 h-5 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                      </svg>
                      <span className="font-bold">{university.supporters_count}</span> supporters
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="px-5 pb-3 flex-grow">
                <p className="text-gray-600 mb-5 line-clamp-3 leading-relaxed text-sm md:text-base">{university.description}</p>
              </div>
              
              <div className="px-5 pb-5">
                {voteSuccess && voteSuccess.id === university.id && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-3 p-2 bg-green-50 border border-green-200 rounded-md text-green-700 text-center font-medium"
                  >
                    {voteSuccess.message}
                  </motion.div>
                )}
                
                {voteError && voteError.id === university.id && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-3 p-2 bg-red-50 border border-red-200 rounded-md text-red-700 text-center font-medium"
                  >
                    {voteError.message}
                  </motion.div>
                )}
                
                <div className="flex gap-2 sm:gap-3">
                  <button
                    id={`vote-btn-${university.id}`}
                    onClick={(e) => handleVoteClick(university.id, e)}
                    className={`flex-1 py-2 sm:py-3 px-2 rounded-lg font-medium text-center transition-all duration-300 text-sm md:text-base ${
                      university.user_has_voted
                        ? 'bg-green-100 text-green-700 border border-green-200 flex justify-center items-center gap-1 sm:gap-2'
                        : votingInProgress === university.id
                          ? 'bg-yellow-400 text-white flex justify-center items-center gap-1 sm:gap-2'
                          : 'bg-yellow-500 text-white hover:bg-yellow-600 shadow-md hover:shadow-lg'
                    }`}
                    disabled={university.user_has_voted || votingInProgress !== null}
                  >
                    {university.user_has_voted ? (
                      <>
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                        <span className="hidden sm:inline">Déjà supporté</span>
                        <span className="sm:hidden">Supporté</span>
                      </>
                    ) : votingInProgress === university.id ? (
                      <>
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 animate-spin flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="hidden sm:inline">Vote en cours...</span>
                        <span className="sm:hidden">Vote...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 hidden sm:inline flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                          <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z"></path>
                        </svg>
                        <span className="truncate">Supporter</span>
                      </>
                    )}
                  </button>
                  
                  <button 
                    onClick={(e) => navigateToUniversityDetails(university.id, e)}
                    className="py-2 sm:py-3 px-3 sm:px-4 rounded-lg font-medium text-center transition-all duration-300 border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 shadow-sm flex items-center justify-center gap-1 text-sm md:text-base flex-shrink-0"
                    aria-label="Voir les détails de l'université"
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <span className="hidden sm:inline">Détails</span>
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
      </div>
      
      {/* Boîte de dialogue de confirmation pour le vote */}
      <Confirm
        isOpen={confirmVote.isOpen}
        title="Confirmer votre vote"
        message={`Êtes-vous sûr de vouloir supporter ${confirmVote.universityName} ? Cette action ne peut pas être annulée.`}
        confirmText="Oui, supporter"
        cancelText="Annuler"
        onConfirm={() => confirmVote.universityId && processVote(confirmVote.universityId)}
        onCancel={() => setConfirmVote({isOpen: false, universityId: null, universityName: ''})}
        variant="warning"
      />
    </div>
  );
};
